import { useRef, useState, useEffect } from 'react';

/**
 * BlockReveal — Text appears behind a sliding colored block.
 * 1. Block slides in from left → right (covering area)
 * 2. Text appears, block slides out to the right
 * 
 * Props:
 *   color: block color (default: primary green)
 *   delay: delay before animation starts (ms)
 *   duration: total animation time (ms)
 *   children: content to reveal
 *   className: additional classes
 *   once: only animate once (default true)
 */
const BlockReveal = ({ children, color = '#02d871', delay = 0, duration = 900, className = '', once = true }) => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          const timer = setTimeout(() => {
            setStarted(true);
            setTimeout(() => setRevealed(true), duration * 0.5);
          }, delay);
          return () => clearTimeout(timer);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay, duration, started]);

  return (
    <span
      ref={ref}
      className={`block-reveal-wrapper ${className}`}
      style={{ '--br-color': color, '--br-duration': `${duration}ms` }}
      data-testid="block-reveal"
    >
      <span className={`block-reveal-content ${revealed ? 'block-reveal-visible' : ''}`}>
        {children}
      </span>
      <span className={`block-reveal-mask ${started ? 'block-reveal-animate' : ''}`} />
    </span>
  );
};

export default BlockReveal;
